/*
 * Lineup wood-strip scroll-linked crossfade (Mike: 2026-05-12)
 * --------------------------------------------------------------
 * Drives a luxury-grade crossfade between two background images on each
 * .strip[data-strip-crossfade="true"] as the user scrolls past it.
 *
 * Behaviour:
 *   - Scroll progress is calculated per-strip relative to the viewport:
 *       p = 0 when the strip's top is at the bottom of the viewport (just entering)
 *       p = 1 when the strip's bottom is at the top of the viewport (just leaving)
 *   - Secondary-layer opacity is a smooth eased curve that peaks at p ~= 0.5:
 *       opacity = easeInOutSine( 1 - |2p - 1| )
 *     -> 0 at entry, 1 at centre, 0 at exit. Looks like a "reveal".
 *   - A subtle scale relaxation from 1.04 -> 1.00 -> 1.04 adds depth without
 *     being a graphic Ken Burns parallax.
 *   - Honours prefers-reduced-motion (does nothing).
 *   - Uses IntersectionObserver to skip work entirely when off-screen.
 *   - Single shared rAF loop, no per-strip rAF storm.
 */
(function () {
  'use strict';

  // Respect user motion preference.
  if (
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  const strips = Array.prototype.slice.call(
    document.querySelectorAll('.strip[data-strip-crossfade="true"]')
  );
  if (strips.length === 0) return;

  // State per strip.
  // progressEl is the element we measure scroll progress against:
  //   - For sticky-pinned strips, the wrapper .strip-stick (which actually scrolls).
  //   - For non-sticky strips, the strip itself.
  const state = strips.map(function (el) {
    const sticky = el.parentElement && el.parentElement.classList.contains('strip-stick')
      ? el.parentElement
      : null;
    return {
      el: el,
      progressEl: sticky || el,
      isSticky: !!sticky,
      primary: el.querySelector('.strip-img__layer.is-primary'),
      secondary: el.querySelector('.strip-img__layer.is-secondary'),
      visible: false,
      progress: 0,
      lastOpacity: -1,
      lastScale: -1
    };
  }).filter(function (s) { return s.secondary && s.primary; });

  if (state.length === 0) return;

  // Easing: smooth in/out -- gives the slow-luxury feel rather than linear.
  function easeInOutSine(t) {
    return -(Math.cos(Math.PI * t) - 1) / 2;
  }

  // Three-act curve: primary settles, secondary reveals + holds briefly, primary settles.
  // We compress the actual transition into the middle ~30% of the scroll pass
  // and give the user a long, calm read on the primary image on either side.
  // The secondary now also holds at 100% for a short window between fade-in and
  // fade-out -- Mike's tweak 2026-05-12: "hold a tiny amount longer on secondary".
  //
  // Acts:
  //   p  0.00 -> 0.38   :: primary at 100% (long settle as the strip enters)
  //   p  0.38 -> 0.46   :: fade in:  primary -> secondary
  //   p  0.46 -> 0.54   :: HOLD secondary at 100% (the "look at me" beat)
  //   p  0.54 -> 0.62   :: fade out: secondary -> primary
  //   p  0.62 -> 1.00   :: primary at 100% (long settle as the strip exits)
  //
  // This gives each strip three distinct visual moments: read, transform,
  // settle. The hold adds a tiny intentional pause on the secondary.
  function revealCurve(p) {
    if (p < 0) p = 0; else if (p > 1) p = 1;
    const FADE_IN_START  = 0.38;
    const FADE_IN_END    = 0.46;
    const HOLD_END       = 0.54;
    const FADE_OUT_END   = 0.62;
    if (p < FADE_IN_START) return 0;
    if (p >= FADE_OUT_END) return 0;
    if (p < FADE_IN_END) {
      return easeInOutSine((p - FADE_IN_START) / (FADE_IN_END - FADE_IN_START));
    }
    if (p < HOLD_END) {
      return 1;
    }
    return easeInOutSine(1 - (p - HOLD_END) / (FADE_OUT_END - HOLD_END));
  }

  // Calculate scroll progress of one element (strip or strip-stick) vs the viewport.
  // Returns: 0 when element top is at viewport bottom (just entering)
  //          1 when element bottom is at viewport top  (just leaving)
  // For sticky wrappers, this gives us the real scroll travel even though
  // the visible strip itself is pinned in place.
  function calcProgress(el, vh) {
    const rect = el.getBoundingClientRect();
    const travel = vh + rect.height;
    const travelled = vh - rect.top;
    return travelled / travel;
  }

  let ticking = false;
  let cachedVh = window.innerHeight;

  function update() {
    for (let i = 0; i < state.length; i++) {
      const s = state[i];
      if (!s.visible) continue;

      const p = calcProgress(s.progressEl, cachedVh);
      const opacity = revealCurve(p);

      // Scale relaxes from 1.04 at entry/exit toward 1.00 at peak --
      // gentle, expensive-feeling. Tied to the same curve so it's coherent.
      const scale = 1.04 - opacity * 0.04;

      // Only write to DOM if the value moved meaningfully (~1% threshold)
      if (Math.abs(opacity - s.lastOpacity) > 0.005) {
        s.secondary.style.opacity = opacity.toFixed(3);
        s.lastOpacity = opacity;
      }
      if (Math.abs(scale - s.lastScale) > 0.001) {
        const t = 'scale(' + scale.toFixed(4) + ')';
        s.primary.style.transform = t;
        s.secondary.style.transform = t;
        s.lastScale = scale;
      }
    }
    ticking = false;
  }

  function requestUpdate() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  }

  // Only run the rAF tick when at least one strip's progress element is in view.
  // We observe progressEl (the sticky wrapper when sticky, the strip otherwise)
  // so the observer fires for the full duration of the scroll runway.
  const io = new IntersectionObserver(function (entries) {
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      const s = state.find(function (x) { return x.progressEl === e.target; });
      if (!s) continue;
      s.visible = e.isIntersecting;
      if (!s.visible) {
        if (s.lastOpacity !== 0) {
          s.secondary.style.opacity = '0';
          s.lastOpacity = 0;
        }
      }
    }
    requestUpdate();
  }, {
    rootMargin: '0px 0px 0px 0px',
    threshold: [0, 0.01, 0.5, 0.99, 1]
  });

  state.forEach(function (s) { io.observe(s.progressEl); });

  // Listen passively to scroll + resize.
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', function () {
    cachedVh = window.innerHeight;
    requestUpdate();
  }, { passive: true });

  // Initial paint.
  requestUpdate();
})();
